import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, DatePicker, Modal } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { toast } from "react-toastify";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import Table from "../../core/common/dataTable/promotiontable";
import { API_BASE_PATH } from "../../environment";
import {
  fetchPromotionsByEmpIdAndDepartment,
  fetchDesignationNames,
  Promotiontype,
} from "../../core/api/usegetPromotion";
import { createPromotion } from "../../core/api/usepostpromotion";
import { deletePromotion } from "../../core/api/usePromotionDelete";
import { updatePromotion } from "../../core/api/useUpdatePromotion";
import {
  fetchTablePromotions,
  TablePromotion,
} from "../../core/api/useTablePromotion";
import { all_routes } from "../router/all_routes";
import { useAuthToken } from "../../core/data/redux/useAuthToken";
import { usePermission } from "../../core/api/useRolePermission";
import { fetchEmployeeImage } from "../../core/api/fetchEmployeeImage";

const Promotion = () => {
  const [promotionTable, setPromotionTable] = useState<TablePromotion[]>([]);
  const [designationList, setDesignationList] = useState<string[]>([]);
  const [userInfo, setUserInfo] = useState<Promotiontype[]>([]);
  const [employeeIds, setEmployeeIds] = useState<string[]>([]);
  const [usernames, setUsernames] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false); // Added loading state
  const empToken = useAuthToken();

  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");
  const [selectedUsername, setSelectedUsername] = useState<string>("");
  const [promotionFrom, setPromotionFrom] = useState<string>("");
  const [promotionTo, setPromotionTo] = useState<string>("");
  const [promotionDate, setPromotionDate] = useState<Dayjs | null>(null);
  const [promotionError, setPromotionError] = useState("");
  const [editingPromotion, setEditingPromotion] =
    useState<TablePromotion | null>(null);
  const [editFrom, setEditFrom] = useState("");
  const [editTo, setEditTo] = useState("");
  const [editDate, setEditDate] = useState<Dayjs | null>(null);
  const [deleteEmployeeId, setDeleteEmployeeId] = useState<string>("");
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [empImages, setEmpImages] = useState<Record<string, string>>({});

  // Permissions
  const canCreate = usePermission("Promotion", "create");
  const canUpdate = usePermission("Promotion", "write");
  const canDelete = usePermission("Promotion", "delete");
  const canRead = usePermission("Promotion", "read");

  const handlePromotionFromChange = (value: string) => {
    setPromotionFrom(value);
    if (value === promotionTo) {
      setPromotionError("Promotion From and To cannot be the same");
    } else {
      setPromotionError("");
    }
  };

  const handlePromotionToChange = (value: string) => {
    setPromotionTo(value);
    if (value === promotionFrom) {
      setPromotionError("Promotion From and To cannot be the same");
    } else {
      setPromotionError("");
    }
  };

  const handleEditFromChange = (value: string) => {
    setEditFrom(value);
    if (value === editTo) {
      setPromotionError("Promotion From and To cannot be the same");
    } else {
      setPromotionError("");
    }
  };

  const handleEditToChange = (value: string) => {
    setEditTo(value);
    if (value === editFrom) {
      setPromotionError("Promotion From and To cannot be the same");
    } else {
      setPromotionError("");
    }
  };

  useEffect(() => {
    fetchAllPromotionTable();
    fetchDropdownData();
  }, []);

  const fetchAllPromotionTable = async () => {
    setIsLoading(true);
    try {
      const data = await fetchTablePromotions(empToken);
      setPromotionTable(data);
      // Fetch images for all employee IDs in the table
      const uniqueEmployeeIds = new Set(data.map((item) => item.employee_id));
      const imagePromises = Array.from(uniqueEmployeeIds).map((empId) =>
        fetchEmployeeImage(empId, empToken)
      );
      const images = await Promise.all(imagePromises);
      const imageMap = Object.fromEntries(
        Array.from(uniqueEmployeeIds).map((empId, index) => [
          empId,
          images[index],
        ])
      );
      setEmpImages(imageMap);
    } catch (error) {
      console.error("Failed to fetch promotion table:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    setIsLoading(true);
    try {
      const data = await fetchPromotionsByEmpIdAndDepartment(empToken);
      setUserInfo(data);
      const ids = Array.from(new Set(data.map((p) => `${p.employee_id}`)));
      const names = Array.from(new Set(data.map((p) => p.username)));
      setEmployeeIds(ids);
      setUsernames(names);
    } catch (err: any) {
      console.error("Failed to fetch user info:", err);
      if (err.response?.status === 401) {
        toast.error("Authentication failed. Please log in again.");
      } else {
        console.error("Failed to load user information for dropdowns");
      }
    }

    try {
      const designations = await fetchDesignationNames(empToken);
      setDesignationList(designations);
    } catch (err: any) {
      console.error("Failed to fetch designations:", err);
      if (err.response?.status === 401) {
        toast.error("Authentication failed. Please log in again.");
      } else {
        console.error("Failed to load designations for dropdowns");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Autofill based on employee or username
  useEffect(() => {
    if (selectedEmployeeId) {
      const match = userInfo.find(
        (p) => `${p.employee_id}` === selectedEmployeeId
      );
      if (match) {
        setSelectedUsername(match.username);
        setPromotionFrom(match.department);
      }
    }
  }, [selectedEmployeeId, userInfo]);

  useEffect(() => {
    if (selectedUsername) {
      const match = userInfo.find((p) => p.username === selectedUsername);
      if (match) {
        setSelectedEmployeeId(`${match.employee_id}`);
        setPromotionFrom(match.department);
      }
    }
  }, [selectedUsername, userInfo]);
 
  const resetAddForm = () => {
    setSelectedEmployeeId("");
    setSelectedUsername("");
    setPromotionFrom("");
    setPromotionTo("");
    setPromotionDate(null);
    setPromotionError("");
  };

  const resetEditForm = () => {
    if (editingPromotion) {
      setEditFrom(editingPromotion.designation_from);
      setEditTo(editingPromotion.designation_to);
      setEditDate(dayjs(editingPromotion.promotion_date));
    } else {
      setEditFrom("");
      setEditTo("");
      setEditDate(null);
    }
    setPromotionError("");
  };

  const handleAddPromotion = async () => {
    if (!canCreate) {
      toast.warning("You don't have permission to create promotions.");
      return;
    }

    if (
      !selectedEmployeeId ||
      !promotionFrom ||
      !promotionTo ||
      !promotionDate
    ) {
      toast.warning("Please fill all required fields.");
      return;
    }

    if (promotionFrom === promotionTo) {
      toast.warning("Promotion From and To cannot be the same");
      return;
    }

    const payload = {
      employee_id: selectedEmployeeId,
      designation_from: promotionFrom,
      designation_to: promotionTo,
      promotion_date: promotionDate.format("YYYY-MM-DD"),
    };

    try {
      const result = await createPromotion(empToken, payload);
      if (result) {
        await fetchAllPromotionTable();
        resetAddForm();
        const modal = document.getElementById("new_promotion");
        const modalInstance = (window as any).bootstrap.Modal.getInstance(
          modal
        );
        if (modalInstance) modalInstance.hide();
      } else {
        throw new Error("Failed to create promotion");
      }
    } catch (err) {}
  };

  const handleDeleteClick = (employeeId: string) => {
    if (!canDelete) {
      return toast.warning("You don't have permission to delete promotions.");
    }
    setDeleteEmployeeId(employeeId);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (!canDelete) {
      toast.warning("You don't have permission to delete promotions.");
      return;
    }

    if (deleteEmployeeId === "") return;

    const promotionId = `promotion${deleteEmployeeId}`;

    try {
      await deletePromotion(empToken, promotionId);
      await fetchAllPromotionTable();
      setDeleteEmployeeId("");
      setDeleteModalVisible(false);
    } catch (err) {}
  };

  const handleUpdate = async () => {
    if (!canUpdate) {
      toast.warning("You don't have permission to update promotions.");
      return;
    }

    if (!editingPromotion || !editFrom || !editTo || !editDate) {
      toast.warning("Please fill all required fields.");
      return;
    }

    if (editFrom === editTo) {
      toast.warning("Promotion From and To cannot be the same");
      return;
    }

    const updatedData = {
      designation_from: editFrom,
      designation_to: editTo,
      promotion_date: editDate.format("YYYY-MM-DD"),
    };

    try {
      await updatePromotion(
        empToken,
        editingPromotion.employee_id,
        updatedData
      );
      await fetchAllPromotionTable();
      setEditingPromotion(null);
      resetEditForm();
      const modal = document.getElementById("edit_promotion");
      const modalInstance = (window as any).bootstrap.Modal.getInstance(modal);
      if (modalInstance) modalInstance.hide();
    } catch (err) {}
  };

  const columns = [
    {
      title: "S.No",
      dataIndex: "sno",
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Promoted Employee",
      dataIndex: "username",
      render: (text: string, record: TablePromotion) => (
        <div className="d-flex align-items-center">
          <div className="avatar avatar-md me-2">
            <img
              src={empImages[record.employee_id] || `${process.env.PUBLIC_URL}/assets/img/defaultavatar.png`}
              className="rounded-circle"
              alt="/assets/img/defaultavatar.png"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = "/assets/img/defaultavatar.png";
              }}
            />
          </div>
          <h6 className="fw-medium mb-0">{record.username}</h6>
        </div>
      ),
    },
    { title: "Employee ID", dataIndex: "employee_id" },
    { title: "Designation From", dataIndex: "designation_from" },
    { title: "Designation To", dataIndex: "designation_to" },
    {
      title: "Promotion Date",
      dataIndex: "promotion_date",
      render: (date: string) => dayjs(date).format("DD-MM-YYYY"),
    },
    {
      title: "Actions",
      render: (_: any, record: TablePromotion) => (
        <div className="action-icon d-inline-flex">
          <Link
            to="#"
            className="me-2"
            onClick={(e) => {
              if (!canUpdate) {
                toast.warning("You don't have permission to edit promotions.");
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              setEditingPromotion(record);
              setEditFrom(record.designation_from);
              setEditTo(record.designation_to);
              setEditDate(dayjs(record.promotion_date));
              setPromotionError("");
            }}
            data-bs-toggle={canUpdate ? "modal" : undefined}
            data-bs-target={canUpdate ? "#edit_promotion" : undefined}
          >
            <i className="ti ti-edit" />
          </Link>
          <button
            type="button"
            className="btn btn-link p-0 text-danger"
            onClick={() => handleDeleteClick(record.employee_id)}
          >
            <i className="ti ti-trash" />
          </button>
        </div>
      ),
    },
  ];

  const getModalContainer = () =>
    document.getElementById("edit_promotion") ?? document.body;

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Promotion</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item active">Promotion</li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                <button
                  type="button"
                  className="btn btn-primary px-4 py-2 rounded d-flex align-items-center"
                  onClick={(e) => {
                    if (!canCreate) {
                      toast.warning(
                        "You don't have permission to create promotions."
                      );
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    resetAddForm();
                  }}
                  data-bs-toggle={canCreate ? "modal" : undefined}
                  data-bs-target={canCreate ? "#new_promotion" : undefined}
                >
                  <span className="w-full text-center block">
                    Add Promotion
                  </span>
                </button>
              </div>
              <div className="ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body p-0">
                  <Table
                    dataSource={promotionTable}
                    columns={columns}
                    Selection={false}
                    loading={isLoading} // Added loading state to table
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal fade" id="new_promotion">
          <div className="modal-dialog modal-dialog-centered modal-lg modal-fullscreen-sm-down">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Add Promotion</h4>
                <button
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={resetAddForm}
                />
              </div>
              <form>
                <div className="modal-body pb-0">
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        Promotion For (Username){" "}
                        <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={selectedUsername}
                        onChange={(e) => setSelectedUsername(e.target.value)}
                        required
                        disabled={isLoading} // Disable while loading
                      >
                        <option value="">Select Username</option>
                        {usernames.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        Employee ID <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={selectedEmployeeId}
                        onChange={(e) => setSelectedEmployeeId(e.target.value)}
                        required
                        disabled={isLoading} // Disable while loading
                      >
                        <option value="">Select Employee ID</option>
                        {employeeIds.map((id) => (
                          <option key={id} value={id}>
                            {id}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        Promotion From <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={promotionFrom}
                        onChange={(e) =>
                          handlePromotionFromChange(e.target.value)
                        }
                        required
                        disabled={isLoading} // Disable while loading
                      >
                        <option value="">Select Designation</option>
                        {designationList.map((designation) => (
                          <option key={designation} value={designation}>
                            {designation}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        Promotion To <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={promotionTo}
                        onChange={(e) =>
                          handlePromotionToChange(e.target.value)
                        }
                        required
                        disabled={isLoading} // Disable while loading
                      >
                        <option value="">Select Designation</option>
                        {designationList.map((designation) => (
                          <option key={designation} value={designation}>
                            {designation}
                          </option>
                        ))}
                      </select>
                      {promotionError && (
                        <div className="mt-2">
                          <span className="text-danger">{promotionError}</span>
                        </div>
                      )}
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        Promotion Date <span className="text-danger">*</span>
                      </label>
                      <div className="input-icon-end position-relative">
                        <DatePicker
                          className="form-control datetimepicker"
                          value={promotionDate}
                          onChange={(date) => setPromotionDate(date)}
                          format="DD-MM-YYYY"
                          placeholder="DD-MM-YYYY"
                          disabledDate={(current) =>
                            current && current < dayjs().startOf("day")
                          }
                          disabled={isLoading} // Disable while loading
                        />
                        <span className="input-icon-addon">
                          <i className="ti ti-calendar text-gray-7" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-white border me-2"
                    onClick={resetAddForm}
                    disabled={isLoading}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAddPromotion}
                    disabled={isLoading}
                  >
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="modal fade" id="edit_promotion">
          <div className="modal-dialog modal-dialog-centered modal-md">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Edit Promotion</h4>
                <button
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={resetEditForm}
                />
              </div>
              <form>
                <div className="modal-body pb-0">
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        Promotion From <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={editFrom}
                        onChange={(e) => handleEditFromChange(e.target.value)}
                        required
                        disabled={isLoading} // Disable while loading
                      >
                        <option value="">Select Designation</option>
                        {designationList.map((designation) => (
                          <option key={designation} value={designation}>
                            {designation}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        Promotion To <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={editTo}
                        onChange={(e) => handleEditToChange(e.target.value)}
                        required
                        disabled={isLoading} // Disable while loading
                      >
                        <option value="">Select Designation</option>
                        {designationList.map((designation) => (
                          <option key={designation} value={designation}>
                            {designation}
                          </option>
                        ))}
                      </select>
                      {promotionError && (
                        <div className="mt-2">
                          <span className="text-danger">{promotionError}</span>
                        </div>
                      )}
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        Promotion Date <span className="text-danger">*</span>
                      </label>
                      <div className="input-icon-end position-relative">
                        <DatePicker
                          className="form-control datetimepicker"
                          value={editDate}
                          onChange={(date) => setEditDate(date)}
                          format="DD-MM-YYYY"
                          placeholder="DD-MM-YYYY"
                          disabledDate={(current) =>
                            current && current < dayjs().startOf("day")
                          }
                          disabled={isLoading} // Disable while loading
                        />
                        <span className="input-icon-addon">
                          <i className="ti ti-calendar text-gray-7" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-white border me-2"
                    onClick={resetEditForm}
                    disabled={isLoading}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleUpdate}
                    disabled={isLoading}
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <Modal
          title="Confirm Deletion"
          open={deleteModalVisible}
          onOk={handleConfirmDelete}
          onCancel={() => setDeleteModalVisible(false)}
          okText="Yes, Delete"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
        >
          <p>Are you sure you want to delete this promotion record?</p>
        </Modal>

        <div className="footer d-sm-flex align-items-center justify-content-between bg-white border-top p-3">
          <p className="mb-0">2024 - 2025 © IATT.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              IATT
            </Link>
          </p>
        </div>
      </div>
    </>
  );
};

export default Promotion;
